import type Phaser from "phaser";
import type { ChunkRangeOut, CompiledChunk, SortReach, WorldManifest } from "daedalus-core";
import { REACH_BAND_LAYERS, defaultPlayersAbove } from "daedalus-core";
import type { ChunkTexturePool, PropView, TileAnimator } from "daedalus-renderer-phaser";
import type { LoadedWorld } from "../../editor/world-loader.ts";
import { viewAfterScroll, type ScrolledCamera } from "../camera-view.ts";
import { ChunkPainter } from "../chunk-painter.ts";
import { chunkKey } from "../chunk-key.ts";
import type { ChunkLoading } from "../chunk-loading.ts";
import { gameReport } from "../report.ts";
import type { BandChunks } from "./band.ts";
import { BesideMinimap, type BesidePictureSource } from "./minimap.ts";
import { dropCandidate, preloadRange, type DropPick, type NeighbourPlace, type TileRect } from "./reach.ts";

/**
 * A neighbour chunk's state in its map's grid.
 */
const NONE = 0;
const LOADED = 1;
const FETCHING = 2;

/**
 * How long a neighbour whose chunk or pages did not load waits before it is asked for again, in milliseconds.
 */
const RETRY_MS = 2000;

/**
 * What the ground beside the map is built over: the scene and world it draws in, the owner's height and Players band, the
 * rows the depth sort spans, the tile animator, the chunk loading knobs, the chunk textures shared with the scene's other
 * painters, and the work it hands out (a chunk's fetch, the atlas pages of a neighbour loaded and let go, the clock, and
 * where a failure is told).
 */
export interface NeighbourGroundOptions {
  readonly scene: Phaser.Scene;
  readonly world: LoadedWorld;
  readonly ownerHeight: number;
  readonly ownerBand: number;
  readonly reach: SortReach;
  readonly animator: TileAnimator;
  readonly loading: ChunkLoading;
  readonly textures?: ChunkTexturePool;
  readonly loadChunk: (mapId: string, cx: number, cy: number) => Promise<CompiledChunk>;
  readonly loadPages: (mapId: string) => Promise<void>;
  readonly releasePages: (mapId: string, kept: readonly string[]) => void;
  readonly now: () => number;
  readonly warn: (line: string) => void;
  readonly pictures?: (map: WorldManifest["maps"][number], chunks: ReadonlyMap<string, CompiledChunk>) => BesidePictureSource;
}

/**
 * One neighbour as the page holds it: its chunks' states and data, its atlas pages' state, its painter once the pages are
 * in, and when a failed load may be asked for again.
 */
interface Held {
  readonly place: NeighbourPlace;
  readonly map: WorldManifest["maps"][number];
  readonly states: Uint8Array;
  readonly chunks: Map<string, CompiledChunk>;
  pages: "none" | "loading" | "ready";
  painter: ChunkPainter | null;
  readonly props: Map<string, PropView>;
  retryAt: number;
  warned: boolean;
  loaded: number;
  fetching: number;
  shown: boolean;
}

/**
 * One neighbour's reading for the page's report.
 */
export interface NeighbourMapReading {
  readonly id: string;
  readonly loaded: number;
  readonly fetching: number;
  readonly pages: "none" | "loading" | "ready";
  readonly painted: boolean;
}

/**
 * The ground beside the map for the page's report: every neighbour's reading, how many neighbour chunks were fetched and
 * let go since the stage began, and the camera's bounds in the owner's tiles.
 */
export interface NeighbourReading {
  readonly maps: readonly NeighbourMapReading[];
  readonly fetched: number;
  readonly dropped: number;
  readonly bounds: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number };
}

/**
 * The neighbouring maps' ground drawn beside the map the page stands on (ADR-147 decisions 6, 8 and 9, P1.4-T191). Each
 * frame, after the owner's painter, it works out the view in the owner's tiles: a neighbour whose chunks the view grown by
 * `PRELOAD_TILES` covers has those chunks fetched and its atlas pages loaded, without the loading screen; a neighbour's
 * painter, made once its pages are in, paints its chunks round the camera exactly as the owner's does, in the owner's
 * frame; and one loaded chunk a frame lying wholly more than `UNLOAD_TILES` beyond the view is let go, the furthest first.
 * A neighbour with nothing left loaded lets its painter go and its pages that no map still drawn uses.
 *
 * A frame with nothing coming in allocates nothing: the view, the ranges and the drop pick are kept and filled in place,
 * and the chunk states are typed arrays. A fetch that fails is asked for again after `RETRY_MS`, and told once.
 */
export class NeighbourGround {
  private readonly held: Held[];
  private readonly places: readonly NeighbourPlace[];
  private readonly states: Uint8Array[];
  private readonly seen = { left: 0, top: 0, width: 0, height: 0 };
  private readonly view: TileRect = { left: 0, top: 0, right: 0, bottom: 0 };
  private readonly range: ChunkRangeOut = { minCx: 0, minCy: 0, maxCx: 0, maxCy: 0 };
  private readonly pick: DropPick = { place: -1, chunk: -1 };
  private fetched = 0;
  private dropped = 0;
  private ended = false;

  /**
   * The minimap's ground beside the map, drawn from the chunks held here; null when the ground was made with no pictures.
   */
  public readonly minimap: BesideMinimap | null;

  /**
   * The reading the page's report calls while this ground is mounted (`mountBeside`), made once.
   *
   * @returns The reading.
   */
  public readonly readingOf = (): NeighbourReading => this.reading();

  /**
   * Told the neighbour's id each time one of its chunks lands, is let go, or the neighbour is hidden, so what reads its
   * chunks (the page's band, `band.ts`) reads them again.
   */
  public onChanged: ((mapId: string) => void) | null = null;

  /**
   * Told the neighbour's id when the ground lets it go (its last chunk dropped, or hidden), so what was fetched ahead for
   * it is let go too (`CrossingStage`).
   */
  public onLetGo: ((mapId: string) => void) | null = null;

  /**
   * @param options What the ground is built over.
   * @param places The owner's placed neighbours.
   * @param bounds The camera's bounds in the owner's tiles, for the report.
   */
  public constructor(
    private readonly options: NeighbourGroundOptions,
    places: readonly NeighbourPlace[],
    private readonly bounds: TileRect,
  ) {
    const maps = options.world.manifest.maps;
    this.held = places.map((place) => ({
      place,
      map: maps.find((map) => map.id === place.id) as WorldManifest["maps"][number],
      states: new Uint8Array(place.columns * place.rows),
      chunks: new Map<string, CompiledChunk>(),
      pages: "none",
      painter: null,
      props: new Map<string, PropView>(),
      retryAt: 0,
      warned: false,
      loaded: 0,
      fetching: 0,
      shown: true,
    }));
    this.places = places;
    const pictures = options.pictures;
    this.minimap = pictures === undefined ? null : new BesideMinimap(this.held.map((held) => ({ x: held.place.x, y: held.place.y, pictures: pictures(held.map, held.chunks) })), options.world.manifest.chunkSize);
    this.states = this.held.map((held) => held.states);
  }

  /**
   * Fetches what the view nears, paints what is loaded round the camera, and lets one far chunk go.
   *
   * @param camera The world camera, scrolled and zoomed for the frame about to be drawn.
   */
  public sync(camera: ScrolledCamera): void {
    if (this.held.length === 0) {
      return;
    }
    const tileSize = this.options.world.manifest.tileSize;
    const seen = viewAfterScroll(camera, this.seen);
    const view = this.view;
    view.left = seen.left / tileSize;
    view.top = seen.top / tileSize;
    view.right = (seen.left + seen.width) / tileSize;
    view.bottom = (seen.top + seen.height) / tileSize;
    const now = this.options.now();
    for (let index = 0; index < this.held.length; index += 1) {
      const held = this.held[index] as Held;
      if (!held.shown) {
        continue;
      }
      if (preloadRange(view, held.place, this.options.world.manifest.chunkSize, this.range)) {
        this.fetchRange(held, now);
      }
      if (held.painter === null && held.pages === "ready" && held.loaded > 0) {
        held.painter = this.painterOf(held);
      }
      held.painter?.sync(camera);
    }
    this.dropOne();
  }

  /**
   * Starts the fetches of the range's chunks not held or on their way, and the neighbour's pages when not loaded.
   *
   * @param held The neighbour.
   * @param now The clock, in milliseconds.
   */
  private fetchRange(held: Held, now: number): void {
    if (now < held.retryAt) {
      return;
    }
    if (held.pages === "none") {
      this.loadPages(held);
    }
    const range = this.range;
    const columns = held.place.columns;
    for (let cy = range.minCy; cy <= range.maxCy; cy += 1) {
      for (let cx = range.minCx; cx <= range.maxCx; cx += 1) {
        if (held.states[cy * columns + cx] === NONE) {
          this.fetchChunk(held, cx, cy);
        }
      }
    }
  }

  /**
   * Loads a neighbour's atlas pages; its painter is made at the first frame after they are in that holds a chunk of it,
   * whose layers name the neighbour's Players band when its manifest entry does not.
   *
   * @param held The neighbour.
   */
  private loadPages(held: Held): void {
    held.pages = "loading";
    this.options.loadPages(held.place.id).then(
      () => {
        if (this.ended) {
          return;
        }
        if (held.pages !== "loading") {
          this.options.releasePages(held.place.id, this.drawnBesides(held));
          return;
        }
        held.pages = "ready";
      },
      (error: unknown) => {
        if (!this.ended) {
          held.pages = "none";
          this.failed(held, `atlas pages of ${held.place.id}`, error);
        }
      },
    );
  }

  /**
   * Fetches one chunk of a neighbour.
   *
   * @param held The neighbour.
   * @param cx Chunk column in the neighbour's grid.
   * @param cy Chunk row.
   */
  private fetchChunk(held: Held, cx: number, cy: number): void {
    const index = cy * held.place.columns + cx;
    held.states[index] = FETCHING;
    held.fetching += 1;
    this.fetched += 1;
    this.options.loadChunk(held.place.id, cx, cy).then(
      (chunk) => {
        if (this.ended) {
          return;
        }
        held.fetching -= 1;
        if (!held.shown || held.states[index] !== FETCHING) {
          return;
        }
        held.states[index] = LOADED;
        held.loaded += 1;
        held.chunks.set(chunkKey(cx, cy), chunk);
        held.painter?.arrived(cx, cy);
        this.minimap?.changed(this.held.indexOf(held), cx, cy);
        this.onChanged?.(held.place.id);
      },
      (error: unknown) => {
        if (this.ended) {
          return;
        }
        held.fetching -= 1;
        if (held.states[index] === FETCHING) {
          held.states[index] = NONE;
        }
        this.failed(held, `chunk ${cx},${cy} of ${held.place.id}`, error);
      },
    );
  }

  /**
   * Holds a neighbour back for `RETRY_MS` after a load failed, telling the first failure.
   *
   * @param held The neighbour.
   * @param what What did not load.
   * @param error Why.
   */
  private failed(held: Held, what: string, error: unknown): void {
    held.retryAt = this.options.now() + RETRY_MS;
    if (!held.warned) {
      held.warned = true;
      this.options.warn(`the ground beside the map will be asked for again: ${what} did not load: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * The painter of a neighbour, placed in the owner's frame.
   *
   * @param held The neighbour.
   * @returns The painter.
   */
  private painterOf(held: Held): ChunkPainter {
    const options = this.options;
    const place = held.place;
    const first = held.chunks.values().next().value as CompiledChunk | undefined;
    const layers = first?.layers ?? [];
    const band = held.map.playersAbove ?? defaultPlayersAbove(layers);
    if (layers.length - band > REACH_BAND_LAYERS) {
      options.warn(`${place.id} has ${layers.length - band} layers in its Players band; beside ${options.ownerHeight}-row maps more than ${REACH_BAND_LAYERS} may sort by layer before row within one pixel`);
    }
    const beside = { cx: place.cx, cy: place.cy, x: place.x, y: place.y, ownerHeight: options.ownerHeight, ownerBand: options.ownerBand, reach: options.reach };
    return new ChunkPainter(options.scene, options.world, held.map, held.chunks, options.animator, null, held.props, band, options.loading, options.now, beside, options.textures ?? null);
  }

  /**
   * Lets go of the one loaded chunk lying furthest beyond `UNLOAD_TILES`, unless its painter still has it drawn, and of a
   * neighbour's painter and pages once nothing of it is loaded.
   */
  private dropOne(): void {
    const pick = this.pick;
    if (!dropCandidate(this.view, this.places, this.states, LOADED, this.options.world.manifest.chunkSize, pick)) {
      return;
    }
    const held = this.held[pick.place] as Held;
    const cx = pick.chunk % held.place.columns;
    const cy = Math.floor(pick.chunk / held.place.columns);
    if (held.painter?.holds(cx, cy) === true) {
      return;
    }
    held.states[pick.chunk] = NONE;
    held.loaded -= 1;
    held.chunks.delete(chunkKey(cx, cy));
    held.painter?.forget(cx, cy);
    this.minimap?.changed(pick.place, cx, cy);
    this.onChanged?.(held.place.id);
    this.dropped += 1;
    if (held.loaded === 0 && held.fetching === 0) {
      this.letGo(held);
    }
  }

  /**
   * Lets a neighbour's painter go, and its atlas pages no map still drawn uses; pages still loading are released when
   * they land (`loadPages`).
   *
   * @param held The neighbour.
   */
  private letGo(held: Held): void {
    held.painter?.clear();
    held.painter = null;
    this.onLetGo?.(held.place.id);
    if (held.pages !== "none") {
      held.pages = "none";
      this.options.releasePages(held.place.id, this.drawnBesides(held));
    }
  }

  /**
   * The other neighbours whose pages are held or loading, whose pages a neighbour let go must not release.
   *
   * @param held The neighbour let go.
   * @returns Their ids.
   */
  private drawnBesides(held: Held): string[] {
    return this.held.filter((other) => other !== held && other.pages !== "none").map((other) => other.place.id);
  }

  /**
   * Whether the page holds any chunk of a neighbour it draws: the test of a crossing that needs no loading screen (ADR-147
   * decision 18).
   *
   * @param mapId The neighbour's id.
   * @returns True when at least one of its chunks is loaded and it is shown.
   */
  public holds(mapId: string): boolean {
    const held = this.held.find((candidate) => candidate.place.id === mapId);
    return held !== undefined && held.shown && held.loaded > 0;
  }

  /**
   * A neighbour's loaded chunks, keyed by `chunkKey`, which a crossing hands the world boot so the map reached is not
   * fetched twice.
   *
   * @param mapId The neighbour's id.
   * @returns The chunks, or null for a map that is not a neighbour.
   */
  public chunksHeld(mapId: string): ReadonlyMap<string, CompiledChunk> | null {
    return this.held.find((candidate) => candidate.place.id === mapId)?.chunks ?? null;
  }

  /**
   * A neighbour's collision as the page's band reads it (`band.ts`): each loaded chunk's cells.
   *
   * @param mapId The neighbour's id.
   * @returns The reader, or null for a map that is not a shown neighbour.
   */
  public collisionOf(mapId: string): BandChunks | null {
    const held = this.held.find((candidate) => candidate.place.id === mapId);
    if (held === undefined || !held.shown) {
      return null;
    }
    return (cx, cy) => held.chunks.get(chunkKey(cx, cy))?.collision ?? null;
  }

  /**
   * Takes a neighbour's chunks the page already holds, as the map just left becomes a neighbour at a crossing (ADR-147
   * decision 18: nothing dropped): they are loaded at once, and its pages, which the atlas holds for the map stood on,
   * are ready, so its painter paints them round the camera on the first frame.
   *
   * @param mapId The neighbour's id.
   * @param chunks Its chunks, keyed by `chunkKey`.
   */
  public seed(mapId: string, chunks: ReadonlyMap<string, CompiledChunk>): void {
    const held = this.held.find((candidate) => candidate.place.id === mapId);
    if (held === undefined || !held.shown) {
      return;
    }
    const columns = held.place.columns;
    for (let cy = 0; cy < held.place.rows; cy += 1) {
      for (let cx = 0; cx < columns; cx += 1) {
        const chunk = chunks.get(chunkKey(cx, cy));
        const index = cy * columns + cx;
        if (chunk === undefined || held.states[index] === LOADED) {
          continue;
        }
        held.states[index] = LOADED;
        held.loaded += 1;
        held.chunks.set(chunkKey(cx, cy), chunk);
        this.minimap?.changed(this.held.indexOf(held), cx, cy);
      }
    }
    if (held.loaded > 0 && held.pages === "none") {
      held.pages = "ready";
    }
    this.onChanged?.(mapId);
  }

  /**
   * Shows or hides a neighbour (ADR-147 decision 26): one whose `edgeNear` names another version than the page holds is
   * not drawn, its painter and chunks let go, until one names the page's version again.
   *
   * @param mapId The neighbour's id.
   * @param shown Whether it is drawn.
   */
  public show(mapId: string, shown: boolean): void {
    const index = this.held.findIndex((candidate) => candidate.place.id === mapId);
    const held = this.held[index];
    if (held === undefined || held.shown === shown) {
      return;
    }
    held.shown = shown;
    if (shown) {
      return;
    }
    for (let chunk = 0; chunk < held.states.length; chunk += 1) {
      if (held.states[chunk] === LOADED) {
        this.minimap?.changed(index, chunk % held.place.columns, Math.floor(chunk / held.place.columns));
      }
    }
    held.states.fill(NONE);
    held.loaded = 0;
    held.chunks.clear();
    this.letGo(held);
    this.onChanged?.(mapId);
  }

  /**
   * The ground's reading for the report, built when asked.
   *
   * @returns The reading.
   */
  public reading(): NeighbourReading {
    const bounds = this.bounds;
    return {
      maps: this.held.map((held) => {
        const range = held.painter?.range ?? null;
        return { id: held.place.id, loaded: held.loaded, fetching: held.fetching, pages: held.pages, painted: range !== null && range.maxCx >= range.minCx };
      }),
      fetched: this.fetched,
      dropped: this.dropped,
      bounds: { left: bounds.left, top: bounds.top, right: bounds.right, bottom: bounds.bottom },
    };
  }

  /**
   * Lets go of everything, for a stage ending: every painter's textures and views, and no load that lands later is taken;
   * the report stops reading it.
   * The pages stay with the atlas holder, which the next stage's boot sorts.
   */
  public clear(): void {
    this.ended = true;
    if (gameReport.neighbours === this.readingOf) {
      gameReport.neighbours = () => null;
    }
    for (const held of this.held) {
      held.painter?.clear();
      held.painter = null;
      held.chunks.clear();
    }
  }
}
